import { Title } from '@portal_components/components/Title';
import { useDefinedParams } from '@/hooks/useDefinedParams';
import { useNavigate } from 'react-router-dom';
import { FormMultiButtonGroup } from '@portal_components/components/FormMultiButtonGroup';
import * as Yup from 'yup';
import { FormButton } from '@portal_components/components/FormButton';
import { useAppDispatch, useAppSelector } from '@/store';
import { setJobDetails } from '@/reducers/jbpGeneralSlice';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { IndicatorType } from '@portal_components/components/WorkflowIndicator';
import { FormWorkflowView } from '@portal_components/formWorkflow/FormWorkflowView';

type BookJobCommunicationsFormValues = {
  smsConfirmationRequired: boolean;
  smsReminderRequired: boolean;
  smsConfirmationNumber?: string;
};

export const FormBookJobCommunications = () => {
  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);
  const navigate = useNavigate();
  const { jobDetails } = useAppSelector(state => state.jbp);
  const dispatch = useAppDispatch();

  const handleSubmit = (values: BookJobCommunicationsFormValues) => {
    if (!jobDetails) return;
    dispatch(setJobDetails({ ...jobDetails, ...values }));
    navigate(`/bookingResult/${mpan}/${serviceLine}`);
  };

  if (!jobDetails) return null;
  return (
    <div className="ml-10 pb-4 mb-10">
      <Title className="mt-7 mb-5" text={`Complete Booking for: ${mpan}`} />
      <FormWorkflowView<BookJobCommunicationsFormValues>
        onSubmit={handleSubmit}
        nodes={[
          {
            element: (
              <FormMultiButtonGroup
                name="smsConfirmationRequired"
                title="Do you require confirmation via SMS of the booking of this job?"
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ),
            validationSchema: {
              smsConfirmationRequired: Yup.boolean().required('Please select an option')
            }
          },
          {
            element: (
              <FormMultiButtonGroup
                name="smsReminderRequired"
                title="Do you require an appointment reminder via SMS?"
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ),
            validationSchema: {
              smsReminderRequired: Yup.boolean().required('Please select an option'),
              smsConfirmationNumber: Yup.string().when(['smsConfirmationRequired', 'smsReminderRequired'], {
                is: (smsConfirmationRequired: boolean, smsReminderRequired: boolean) => smsConfirmationRequired || smsReminderRequired,
                then: schema => schema.required('Please enter a contact number').matches(/^07\d{8}\d?$/, 'Contact number must contain only digits'),
                otherwise: schema => schema.notRequired()
              })
            },
            children: (values: BookJobCommunicationsFormValues) => {
              if (values.smsConfirmationRequired || values.smsReminderRequired) {
                return {
                  indicatorType: IndicatorType.Default,
                  element: (
                    <div>
                      <Title className="mt-7 mb-5" text="Please confirm the mobile number to be used for SMS notifications" />
                      <FormTextInput name="smsConfirmationNumber" title="Contact Number" mandatory />
                    </div>
                  )
                };
              }
            }
          },
          {
            element: (
              <div>
                <Title className="mt-7 mb-5" text="Complete your booking" />
                <p className="mb-7">Take a moment to ensure your entries are correct and then go ahead, complete the booking</p>
                <FormButton type="submit" primary icon>
                  Book Job
                </FormButton>
              </div>
            )
          }
        ]}
      />
    </div>
  );
};
